import axios from 'axios';
import { apiClient } from './api';

/**
 * Drop blank filter values before sending them as query params.
 *
 * The admin screens keep unset filters as empty strings in state and hand the
 * whole filter object to the export calls, so without this the request carries
 * `status=&type=` noise.
 */
function cleanExportParams<T extends Record<string, any>>(params: T): Record<string, any> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );
}

// Types for billing admin
export interface Transaction {
  TransactionID: string;
  ExternalID: string;
  AccountID: string;
  PaymentMethodID?: string;
  InvoiceRefNo?: string;
  Type: 'payment' | 'refund' | 'authorization' | 'topup';
  Status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  Amount: number;
  Currency: string;
  RazorpayOrderID?: string;
  RazorpayPaymentID?: string;
  RequiresAction: boolean;
  FailureCode?: string;
  FailureMessage?: string;
  CreatedAt: string;
  UpdatedAt: string;
  // Joined fields
  CustomerName?: string;
  CustomerEmail?: string;
  PaymentMethodLast4?: string;
  PaymentMethodBrand?: string;
  // Wallet top-up rows (successful credits come from the wallet ledger and
  // have Source 'wallet' — no transaction detail page; failed attempts come
  // from tblTransactions and keep Source 'transaction'). Storefront card
  // orders have Source 'order': ExternalID is the order number, and they have
  // no detail page or refund action here (refunds go through the Orders screen).
  IsWalletTopup?: boolean;
  Source?: 'transaction' | 'wallet' | 'order';
  CreditAmount?: number;
  // False for top-ups recorded before the paid amount was stored: Amount then
  // holds the credit, and the screens show the paid amount as not recorded.
  PaidAmountRecorded?: boolean;
}

export interface Invoice {
  InvoiceID: string;
  InvoiceNumber: string;
  AccountID: string;
  SubscriptionID?: string;
  QuotationRefNo?: string;
  Status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  Subtotal: number;
  Tax: number;
  Total: number;
  AmountPaid: number;
  AmountDue: number;
  Currency: string;
  DueDate?: string;
  PaidAt?: string;
  Description?: string;
  CreatedAt: string;
  UpdatedAt?: string;
  // Joined fields
  CustomerName?: string;
  CustomerEmail?: string;
}

export interface Subscription {
  SubscriptionID: string;
  AccountID: string;
  PlanID: string;
  Status: 'active' | 'cancelled' | 'past_due' | 'trialing' | 'incomplete';
  StripeSubscriptionID?: string;
  CurrentPeriodStart?: string;
  CurrentPeriodEnd?: string;
  CancelAtPeriodEnd: boolean;
  TrialStart?: string;
  TrialEnd?: string;
  CreatedAt: string;
  UpdatedAt?: string;
  // Joined fields
  CustomerName?: string;
  CustomerEmail?: string;
  PlanName?: string;
  PlanCode?: string;
  PlanAmount?: number;
}

export interface Plan {
  PlanID: string;
  Name: string;
  Code: string;
  Description?: string;
  Amount: number;
  Currency: string;
  IntervalUnit: 'day' | 'week' | 'month' | 'year';
  IntervalCount: number;
  TrialDays: number;
  StripePriceID?: string;
  IsActive: boolean;
  CreatedAt: string;
}

export interface BillingDashboardStats {
  totalRevenue: number;
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  openInvoices: number;
  paidInvoices: number;
  activeSubscriptions: number;
  monthlyRecurringRevenue: number;
  recentTransactions: Transaction[];
  revenueByMonth: Array<{ month: string; revenue: number }>;
  transactionsByStatus: Record<string, number>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

class BillingAdminService {
  /**
   * Get billing dashboard statistics
   */
  async getDashboardStats(): Promise<BillingDashboardStats> {
    const response = await apiClient.get('/admin/billing/dashboard');
    return response.data;
  }

  /**
   * Get all transactions with pagination and filters
   */
  async getTransactions(params: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
  } = {}): Promise<PaginatedResponse<Transaction>> {
    const response = await apiClient.get('/admin/billing/transactions', params);
    return {
      data: response.data.transactions || response.data,
      pagination: response.data.pagination || {
        currentPage: params.page || 1,
        totalPages: 1,
        totalItems: Array.isArray(response.data) ? response.data.length : 0,
        itemsPerPage: params.limit || 50
      }
    };
  }

  /**
   * Get transaction details
   */
  async getTransactionDetails(transactionId: string): Promise<Transaction> {
    const response = await apiClient.get(`/admin/billing/transactions/${transactionId}`);
    return response.data;
  }

  /**
   * Process a refund
   */
  async processRefund(transactionId: string, data: {
    amount?: number;
    reason?: string;
  }): Promise<{ refundId: string; status: string }> {
    const response = await apiClient.post(`/admin/billing/transactions/${transactionId}/refund`, data);
    return response.data;
  }

  /**
   * Get all invoices with pagination and filters
   */
  async getInvoices(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<PaginatedResponse<Invoice>> {
    const response = await apiClient.get('/admin/billing/invoices', params);
    return {
      data: response.data.invoices || response.data,
      pagination: response.data.pagination || {
        currentPage: params.page || 1,
        totalPages: 1,
        totalItems: Array.isArray(response.data) ? response.data.length : 0,
        itemsPerPage: params.limit || 50
      }
    };
  }

  /**
   * Get invoice details
   */
  async getInvoiceDetails(invoiceId: string): Promise<Invoice> {
    const response = await apiClient.get(`/admin/billing/invoices/${invoiceId}`);
    return response.data;
  }

  /**
   * Void an invoice
   */
  async voidInvoice(invoiceId: string): Promise<void> {
    await apiClient.post(`/admin/billing/invoices/${invoiceId}/void`);
  }

  /**
   * Send invoice reminder
   */
  async sendInvoiceReminder(invoiceId: string): Promise<void> {
    await apiClient.post(`/admin/billing/invoices/${invoiceId}/remind`);
  }

  /**
   * Download invoice as PDF
   */
  async downloadInvoicePDF(invoiceId: string, invoiceNumber: string): Promise<void> {
    // Use axios directly for blob responses since apiClient.get doesn't support responseType
    const token = localStorage.getItem('dasoonegg-auth');
    const authData = token ? JSON.parse(token) : {};
    const authToken = authData.state?.token;

    const response = await axios.get(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'}/admin/billing/invoices/${invoiceId}/pdf`,
      {
        responseType: 'blob',
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      }
    );

    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get all subscriptions with pagination and filters
   */
  async getSubscriptions(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    planCode?: string;
  } = {}): Promise<PaginatedResponse<Subscription>> {
    const response = await apiClient.get('/admin/billing/subscriptions', params);
    return {
      data: response.data.subscriptions || response.data,
      pagination: response.data.pagination || {
        currentPage: params.page || 1,
        totalPages: 1,
        totalItems: Array.isArray(response.data) ? response.data.length : 0,
        itemsPerPage: params.limit || 50
      }
    };
  }

  /**
   * Get subscription details
   */
  async getSubscriptionDetails(subscriptionId: string): Promise<Subscription> {
    const response = await apiClient.get(`/admin/billing/subscriptions/${subscriptionId}`);
    return response.data;
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId: string, immediately: boolean = false): Promise<void> {
    await apiClient.delete(`/admin/billing/subscriptions/${subscriptionId}`, {
      params: { cancelAtPeriodEnd: !immediately }
    });
  }

  /**
   * Get all plans
   */
  async getPlans(): Promise<Plan[]> {
    const response = await apiClient.get('/admin/billing/plans');
    return response.data;
  }

  /**
   * Create a new plan
   */
  async createPlan(data: Omit<Plan, 'PlanID' | 'CreatedAt'>): Promise<Plan> {
    const response = await apiClient.post('/admin/billing/plans', data);
    return response.data;
  }

  /**
   * Update a plan
   */
  async updatePlan(planId: string, data: Partial<Plan>): Promise<Plan> {
    const response = await apiClient.put(`/admin/billing/plans/${planId}`, data);
    return response.data;
  }

  /**
   * Delete/deactivate a plan
   */
  async deletePlan(planId: string): Promise<void> {
    await apiClient.delete(`/admin/billing/plans/${planId}`);
  }

  /**
   * Export transactions to CSV
   */
  async exportTransactionsCSV(params: {
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<Blob> {
    return apiClient.getBlob('/admin/billing/transactions/export', cleanExportParams(params));
  }

  /**
   * Export invoices to CSV
   */
  async exportInvoicesCSV(params: {
    status?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<Blob> {
    return apiClient.getBlob('/admin/billing/invoices/export', cleanExportParams(params));
  }
}

export const billingAdminService = new BillingAdminService();
